import React, { useState, useRef, useEffect } from "react";
import { Select, Input, Button,  message } from "antd";
import { Upload } from "lucide-react";
import { countries } from "../../../../../constant/countery";
import { timeZone } from "../../../../../constant/timeZone";
import { getRequest, putRequest, uploadFile } from "../../../../../service/index";
const { Option } = Select;

const CompanyProfile = () => {
  const COMPANY_TYPES = [
    { label: "Private Limited Company", value: "private" },
    { label: "Public Limited Company", value: "public" },
    { label: "Call Center", value: "call_center" },
    { label: "IT Company", value: "it" },
    { label: "Consulting Firm", value: "consulting" },
    { label: "Manufacturing Company", value: "manufacturing" },
    { label: "Retail Company", value: "retail" },
    { label: "Healthcare Organization", value: "healthcare" },
    { label: "Educational Institution", value: "education" },
    { label: "Financial Services", value: "financial" },
    { label: "Other", value: "other" }
  ];

  const fileRef = useRef(null);
  const [companyId, setCompanyId] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({
    companyName: "",
    companyType: "",
    website: "",
    email: "",
    country: "",
    timeZone: "",
    currency: "", 
    decimal: true,
    allowEmployeeLifeCycle: false,
    nationalIdMandatory: false,
    salaryMethod: "",
    companyLogo: null,
    employeeLoan: false,
    loanType: "percentage", 
    loanValue: "",
    returnPolicy: "salary", 
    returnAmount: "",
    returnMonths: "",
    maxLoans: "",
  });

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const response = await getRequest("/api/companies/new");
        if (response?.companies?.[0]) {
          const companyData = response.companies[0];
          setCompanyId(companyData._id);
          setFormData(prev => ({
            ...prev,
            companyName: companyData.companyName || "",
            companyType: companyData.companyType || "",
            website: companyData.website || "",
            email: companyData.emailAddress || "",
            country: companyData.country || "",
            timeZone: companyData.timeZone || "",
            currency: companyData.currency || "",
            decimal: typeof companyData.decimal === "boolean" ? companyData.decimal : true,
            salaryMethod: companyData.saleryCalculation || "",
            companyLogo: companyData.logo ? { url: companyData.logo } : null
          }));
        }
      } catch (error) {
        console.error("Error fetching company data:", error);
      }
    };

    fetchCompanyData();
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const response = await uploadFile(file);
      if (response?.url) {
        setFormData(prev => ({
          ...prev,
          companyLogo: { url: response.url }
        }));
        message.success('Logo uploaded successfully');
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      message.error('Failed to upload logo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      const updateData = {
        companyName: formData.companyName,
        companyType: formData.companyType,
        website: formData.website,
        emailAddress: formData.email,
        country: formData.country,
        timeZone: formData.timeZone,
        currency: formData.currency,
        decimal: formData.decimal,
        saleryCalculation: formData.salaryMethod,
        logo: formData.companyLogo?.url || null,
      };

      const response = await putRequest(`/api/companies/new?companyId=${companyId}`, updateData);
      if (response) {
        message.success('Company data updated successfully');
      }
    } catch (error) {
      console.error("Error updating company data:", error);
      message.error('Failed to update company data');
    }
  };

  const getCurrencySymbol = () => {
    if (!formData.currency) return "$";
    const selectedCountry = countries.find(country => country.currency === formData.currency);
    return selectedCountry ? selectedCountry.currencySymbol : "$";
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="bg-white rounded-md p-4">
      <div className="grid grid-cols-12 gap-4 border border-gray-200 rounded-md p-4 relative">
        <div className="absolute -top-2 bg-white left-2 px-1">
          <h4 className="text-active-color text-md font-semibold">
            Company Information
          </h4>
        </div>
        <div className="col-span-6">
          <label className="font-semibold text-md">Company Name</label>
          <Input
            name="companyName"
            value={formData.companyName}
            onChange={handleInputChange}
          />
          <p className="text-md text-gray-400">
            This is your registered business name.
          </p>
        </div>
        <div className="col-span-6">
          <label className="font-semibold text-md">Company Type</label>
          <Select
            value={formData.companyType}
            onChange={(value) =>
              handleInputChange({ target: { name: "companyType", value } })
            }
            className="w-full"
          >
            <Option value="">Select One</Option>
            {COMPANY_TYPES.map((type) => (
              <Option key={type.value} value={type.value}>
                {type.label}
              </Option>
            ))}
          </Select>
        </div>
        <div className="col-span-6">
          <label className="font-semibold text-md">Country</label>
          <Select
            value={formData.country}
            onChange={(value) =>
              handleInputChange({ target: { name: "country", value } })
            }
            className="w-full"
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            <Option value="">Select Country</Option>
            {countries.map((country) => (
              <Option key={country.value} value={country.value}>
                {country.value}
              </Option>
            ))}
          </Select>
        </div>
        <div className="col-span-6">
          <label className="font-semibold text-md">Time Zone</label>
          <Select
            value={formData.timeZone}
            onChange={(value) =>
              handleInputChange({ target: { name: "timeZone", value } })
            }
            className="w-full"
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) => {
              if (!option?.children) return false;
              const searchStr = input.toLowerCase();
              const timezoneData = timeZone.find(zone => zone.text === option.children);
              if (!timezoneData) return false;
              
              return (
                timezoneData.value.toLowerCase().includes(searchStr) ||
                timezoneData.abbr.toLowerCase().includes(searchStr) ||
                timezoneData.text.toLowerCase().includes(searchStr)
              );
            }}
          >
            <Option value="">Select Time Zone</Option>
            {timeZone.map((zone) => (
              <Option key={zone.value} value={zone.abbr}>
                {zone.text}
              </Option>
            ))}
          </Select>
        </div>
        <div className="col-span-6">
          <label className="font-semibold text-md">Currency</label>
          <Select
            value={formData.currency}
            onChange={(value) =>
              handleInputChange({ target: { name: "currency", value } })
            }
            className="w-full"
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) => {
              if (!option?.label) return false;
              return option.label.toLowerCase().includes(input.toLowerCase());
            }}
            options={[
              { value: "", label: "Select Currency" },
              ...countries.map((country) => ({
                value: country.currency,
                label: `${country.currency} (${country.currencySymbol})`,
                key: `${country.currency}-${country.value}`
              }))
            ]}
          />
        </div>
        <div className="col-span-6">
          <label className="font-semibold text-md">Price in decimal</label>
          <Select
            value={formData.decimal}
            onChange={(value) =>
              handleInputChange({ target: { name: "decimal", value } })
            }
            className="w-full"
          >
            <Option value={false}>No</Option>
            <Option value={true}>Yes</Option>
          </Select>
        </div>
        <div className="col-span-12 flex flex-col">
          <label className="font-semibold text-md">Company Logo</label>
          <div className="flex items-center justify-between">
            <div className="p-4 rounded-md bg-gray-100 flex items-center justify-center w-full max-w-3xs relative">
              <Button
                onClick={() => fileRef.current && fileRef.current.click()}
                loading={isUploading}
                disabled={isUploading}
              >
                <Upload className="h-4 w-4" /> Upload
              </Button>
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              ref={fileRef}
              hidden
            />
            {formData.companyLogo && (
              <div className="mt-2 flex items-center justify-center w-50 h-24 border border-gray-200 rounded bg-white overflow-hidden">
                <img
                  src={formData.companyLogo.url}
                  alt="Company Logo"
                  className="w-full h-full object-contain"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Rest of the component remains unchanged */}
      <div className="grid grid-cols-12 gap-4 border border-gray-200 rounded-md p-4 relative mt-4">
        <div className="absolute -top-2 bg-white left-2 px-1">
          <h4 className="text-active-color text-md font-semibold">
            Contact Information
          </h4>
        </div>
        <div className="col-span-6">
          <label className="font-semibold text-md">Website</label>
          <Input
            name="website"
            value={formData.website}
            onChange={handleInputChange}
          />
        </div>
        <div className="col-span-6">
          <label className="font-semibold text-md">Email</label>
          <Input
            name="email"
            value={formData.email}
            onChange={handleInputChange}
          />
        </div>
      </div>

      
      <div className="flex items-center justify-end gap-3 mt-3">
        <Button 
          onClick={handleUpdate}
          type="primary"
          className="bg-primary text-white"
        >
          Update Company Information
        </Button>
      </div>
    </div>
  );
};

export default CompanyProfile;
